import React, { useEffect, useMemo, useState } from 'react';
import { useSchoolYear } from '../contexts/SchoolYearContext';
import {
  datesFromSchoolYearLabel,
  suggestNextSchoolYearLabel,
} from '../utils/schoolYearDates';
import { SchoolYearCard, SchoolYearStatusChip, schoolYearFieldStyle } from './SchoolYearCard';

interface SchoolYearSelectorProps {
  /** Persiste la sélection comme année scolaire courante côté backend. */
  persistCurrent?: boolean;
  labels?: {
    schoolYear?: string;
    /** Libellé du champ (à côté du sélecteur). */
    fieldLabel?: string;
    /** Phrase d'explication affichée sous le titre. */
    persistHint?: string;
    /** Variante de la phrase d'explication hors persistance (facultative). */
    hint?: string;
    /** Indicateur pendant l'enregistrement. */
    saving?: string;
    current?: string;
    closed?: string;
    newYear?: string;
    create?: string;
    cancel?: string;
    creating?: string;
    startDate?: string;
    endDate?: string;
    calendarHint?: string;
    closeYear?: string;
    closeYearTitle?: string;
    closeYearHelp?: string;
    activateYear?: string;
    confirmClose?: string;
    closing?: string;
    closeError?: string;
    closeYearNeedNew?: string;
  };
}

export const SchoolYearSelector: React.FC<SchoolYearSelectorProps> = ({ labels = {}, persistCurrent = false }) => {
  const {
    years,
    selectedYear,
    currentYear,
    loading,
    isReadOnly,
    selectSchoolYear,
    setCurrentSchoolYear,
    createSchoolYear,
    closeSchoolYear,
  } = useSchoolYear();

  const [showCreate, setShowCreate] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [showClose, setShowClose] = useState(false);
  const [activateId, setActivateId] = useState<number | ''>('');
  const [closing, setClosing] = useState(false);
  const [closeError, setCloseError] = useState<string | null>(null);
  const [createHint, setCreateHint] = useState<string | null>(null);
  const [savingCurrent, setSavingCurrent] = useState(false);
  const [persistError, setPersistError] = useState<string | null>(null);

  const todayStr = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();

  const L = {
    schoolYear: labels.schoolYear ?? 'Année scolaire',
    fieldLabel: labels.fieldLabel,
    persistHint:
      labels.persistHint ??
      'Cette année devient l\'année courante pour tout le système.',
    hint: labels.hint,
    saving: labels.saving ?? 'Enregistrement…',
    current: labels.current ?? 'Courante',
    closed: labels.closed ?? 'Clôturée (lecture seule)',
    newYear: labels.newYear ?? 'Créer une année',
    create: labels.create ?? 'Créer',
    cancel: labels.cancel ?? 'Annuler',
    creating: labels.creating ?? 'Création…',
    startDate: labels.startDate ?? 'Début',
    endDate: labels.endDate ?? 'Fin',
    calendarHint:
      labels.calendarHint ??
      'Par défaut : 1er septembre → 31 août (modifiable)',
    closeYear: labels.closeYear ?? 'Clôturer l\'année courante',
    closeYearTitle: labels.closeYearTitle ?? 'Clôturer l\'année scolaire',
    closeYearHelp:
      labels.closeYearHelp ??
      'L\'année courante passera en lecture seule. Choisissez l\'année à activer.',
    activateYear: labels.activateYear ?? 'Activer l\'année',
    confirmClose: labels.confirmClose ?? 'Clôturer et activer',
    closing: labels.closing ?? 'Clôture…',
    closeError: labels.closeError ?? 'Erreur lors de la clôture',
    closeYearNeedNew:
      labels.closeYearNeedNew ??
      'Créez d\'abord la nouvelle année (« Nouvelle année »), puis clôturez l\'année courante.',
  };

  const activateCandidates = useMemo(
    () =>
      years.filter(
        (y) =>
          y.status !== 'closed' &&
          currentYear &&
          y.id !== currentYear.id
      ),
    [years, currentYear]
  );

  useEffect(() => {
    if (showCreate && !newLabel && currentYear) {
      const next = suggestNextSchoolYearLabel(currentYear.label);
      setNewLabel(next);
      const d = datesFromSchoolYearLabel(next);
      if (d) {
        setStartDate(d.start_date);
        setEndDate(d.end_date);
      }
    }
  }, [showCreate, currentYear, newLabel]);

  useEffect(() => {
    if (showClose && activateCandidates.length && activateId === '') {
      const sorted = [...activateCandidates].sort((a, b) =>
        (b.start_date || '').localeCompare(a.start_date || '')
      );
      setActivateId(sorted[0].id);
    }
  }, [showClose, activateCandidates, activateId]);

  const onLabelChange = (value: string) => {
    setNewLabel(value);
    const d = datesFromSchoolYearLabel(value);
    if (d) {
      setStartDate(d.start_date);
      setEndDate(d.end_date);
    }
  };

  const handleCreate = async () => {
    if (endDate && endDate < todayStr) {
      setCreateError('Impossible de créer une année scolaire antérieure');
      return;
    }
    setCreating(true);
    setCreateError(null);
    const res = await createSchoolYear({
      label: newLabel.trim() || undefined,
      start_date: startDate || undefined,
      end_date: endDate || undefined,
    });
    setCreating(false);
    if (!res.success) {
      setCreateError(res.error || 'Erreur');
      return;
    }
    setShowCreate(false);
    setNewLabel('');
    setStartDate('');
    setEndDate('');
  };

  const handleClose = async () => {
    if (!currentYear || activateId === '') return;
    setClosing(true);
    setCloseError(null);
    const res = await closeSchoolYear(currentYear.id, Number(activateId));
    setClosing(false);
    if (!res.success) {
      setCloseError(res.error || L.closeError);
      return;
    }
    setShowClose(false);
    setActivateId('');
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _showCloseButton =
    Boolean(currentYear) && currentYear!.status !== 'closed';

  const canCloseNow = activateCandidates.length > 0;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _handleCloseClick = () => {
    if (!canCloseNow) {
      setCreateHint(L.closeYearNeedNew);
      setShowCreate(true);
      return;
    }
    setCreateHint(null);
    setShowClose(true);
  };

  return (
    <SchoolYearCard
      icon="⚙️"
      iconBg="#f3e8ff"
      titleColor="#5e35b1"
      title={L.schoolYear}
      hint={persistCurrent ? L.persistHint : L.hint}
      fieldId="school-year-current"
      fieldLabel={L.fieldLabel}
      field={
        <select
          id="school-year-current"
          value={selectedYear?.id ?? ''}
          disabled={loading || years.length === 0 || savingCurrent}
          onChange={async (e) => {
            const y = years.find((yr) => String(yr.id) === e.target.value);
            if (!y) return;
            selectSchoolYear(y);
            if (persistCurrent) {
              setSavingCurrent(true);
              setPersistError(null);
              const res = await setCurrentSchoolYear(y);
              setSavingCurrent(false);
              if (!res.success) setPersistError(res.error || 'Erreur');
            }
          }}
          style={{ ...schoolYearFieldStyle, cursor: loading ? 'wait' : 'pointer' }}
        >
          {years.map((y) => (
            <option key={y.id} value={y.id}>
              {y.label}
              {y.is_current ? ` (${L.current})` : ''}
              {y.status === 'closed' ? ' — clôturée' : ''}
            </option>
          ))}
        </select>
      }
      actions={
        <>
          {currentYear && (
            <SchoolYearStatusChip
              label={`${L.current} : ${currentYear.label}`}
              tone={selectedYear?.is_current ? 'green' : 'slate'}
            />
          )}
          {savingCurrent && (
            <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 600 }}>{L.saving}</span>
          )}
          {persistError && (
            <span style={{ fontSize: '12px', color: '#b91c1c', fontWeight: 600 }}>{persistError}</span>
          )}
          {isReadOnly && <SchoolYearStatusChip label={L.closed} tone="amber" />}
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
      {/* Clôturer l'année courante — masqué temporairement
        {showCloseButton && (
          <button
            type="button"
            onClick={handleCloseClick}
            title={!canCloseNow ? L.closeYearNeedNew : undefined}
            style={{
              padding: '7px 14px',
              fontSize: '13px',
              borderRadius: '8px',
              border: '2px solid #d97706',
              backgroundColor: '#d97706',
              color: '#fff',
              cursor: 'pointer',
              fontWeight: 600,
              opacity: canCloseNow ? 1 : 0.6,
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              boxShadow: canCloseNow ? '0 1px 4px rgba(217,119,6,0.35)' : 'none',
              transition: 'opacity 0.15s',
            }}
          >
            <span style={{ fontSize: '15px', lineHeight: 1 }}>🔒</span>
            {L.closeYear}
          </button>
        )}
      */}
        <button
          type="button"
          onClick={() => {
            setCreateHint(null);
            setShowCreate((v) => !v);
          }}
          style={{
            padding: '7px 14px',
            fontSize: '13px',
            borderRadius: '8px',
            border: '2px solid #5e35b1',
            backgroundColor: showCreate ? '#5e35b1' : '#ede9fe',
            color: showCreate ? '#fff' : '#5e35b1',
            cursor: 'pointer',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            boxShadow: '0 1px 4px rgba(94,53,177,0.18)',
            transition: 'background-color 0.15s, color 0.15s',
          }}
        >
          <span style={{ fontSize: '16px', lineHeight: 1 }}>＋</span>
          {L.newYear}
        </button>
          </div>
        </>
      }
    >

      {showCreate && (
        <div
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            marginTop: '14px',
            paddingTop: '14px',
            borderTop: '1px solid #f1f5f9',
          }}
        >
          <p style={{ margin: 0, fontSize: '12px', color: '#6b7280' }}>{L.calendarHint}</p>
          {createHint && (
            <p style={{ margin: 0, fontSize: '13px', color: '#b45309', fontWeight: 500 }}>{createHint}</p>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
            <input
              type="text"
              placeholder={currentYear ? `ex. après ${currentYear.label}` : '2026-2027'}
              value={newLabel}
              onChange={(e) => onLabelChange(e.target.value)}
              style={{
                flex: '1 1 120px',
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1px solid #d1d5db',
                fontSize: '14px',
                boxSizing: 'border-box',
              }}
            />
            <label style={{ fontSize: '12px', color: '#374151' }}>{L.startDate}</label>
            <input
              type="date"
              min={todayStr}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{
                padding: '8px 10px',
                borderRadius: '8px',
                border: '1px solid #d1d5db',
                fontSize: '14px',
                boxSizing: 'border-box',
              }}
            />
            <label style={{ fontSize: '12px', color: '#374151' }}>{L.endDate}</label>
            <input
              type="date"
              min={todayStr}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={{
                padding: '8px 10px',
                borderRadius: '8px',
                border: '1px solid #d1d5db',
                fontSize: '14px',
                boxSizing: 'border-box',
              }}
            />
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              type="button"
              disabled={creating}
              onClick={handleCreate}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: '#5e35b1',
                color: '#fff',
                cursor: creating ? 'wait' : 'pointer',
                fontSize: '13px',
                fontWeight: 600,
              }}
            >
              {creating ? L.creating : L.create}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowCreate(false);
                setCreateError(null);
              }}
              style={{
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1px solid #d1d5db',
                backgroundColor: '#fff',
                cursor: 'pointer',
                fontSize: '13px',
              }}
            >
              {L.cancel}
            </button>
          </div>
          {createError && (
            <span style={{ color: '#b91c1c', fontSize: '13px' }}>{createError}</span>
          )}
        </div>
      )}

      {showClose && currentYear && (
        <div
          style={{
            width: '100%',
            padding: '14px',
            marginTop: '4px',
            borderRadius: '10px',
            backgroundColor: '#fffbeb',
            border: '1px solid #fcd34d',
          }}
        >
          <div style={{ fontWeight: 700, color: '#92400e', marginBottom: '8px' }}>
            {L.closeYearTitle} — {currentYear.label}
          </div>
          <p style={{ margin: '0 0 12px', fontSize: '13px', color: '#78350f' }}>{L.closeYearHelp}</p>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>
            {L.activateYear}
          </label>
          <select
            value={activateId}
            onChange={(e) => setActivateId(Number(e.target.value))}
            style={{
              width: '100%',
              maxWidth: '280px',
              padding: '8px 12px',
              borderRadius: '8px',
              border: '1px solid #fcd34d',
              marginBottom: '12px',
              fontSize: '14px',
            }}
          >
            {activateCandidates.map((y) => (
              <option key={y.id} value={y.id}>
                {y.label}
              </option>
            ))}
          </select>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              type="button"
              disabled={closing || activateId === ''}
              onClick={handleClose}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: '#b45309',
                color: '#fff',
                cursor: closing ? 'wait' : 'pointer',
                fontWeight: 600,
                fontSize: '13px',
              }}
            >
              {closing ? L.closing : L.confirmClose}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowClose(false);
                setCloseError(null);
              }}
              style={{
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1px solid #d1d5db',
                backgroundColor: '#fff',
                cursor: 'pointer',
                fontSize: '13px',
              }}
            >
              {L.cancel}
            </button>
          </div>
          {closeError && (
            <p style={{ color: '#b91c1c', fontSize: '13px', marginTop: '8px' }}>{closeError}</p>
          )}
        </div>
      )}
    </SchoolYearCard>
  );
};
